// Components==============
import { ProcessGroups } from 'hooks/useProcessGroups';
import { useRedux } from 'hooks/useRedux';
import { ProjectDetailsContext } from 'pages/Workspace/ProjectDetails/ProjectDetails';
import React, { createContext, useContext } from 'react';
import { groupSparksBySkill } from 'utilities/groupSparksBySkill';
import GridFiller from '../GridFiller/GridFiller';
import './ProjectProcess.module.scss';
import { End, ProcessSteps, Start } from './Steps';
// =========================

type Props = {
  project?: ProjectProjectEntity;
  processGroups?: ProcessGroups;
  inModal?: boolean;
  isEditable?: boolean;
  setIsSaveable?: React.Dispatch<React.SetStateAction<boolean>>;
};

type ProjectProcessContextType = {
  groupedSparks?: {
    [key: string]: {
      name: string;
      sparks: SparkEntity[];
    };
  };
  skillObj?:
    | {
        [key: string]: SkillEntity;
      }
    | undefined;
  filteredProjectSparks?: (SparkEntity | undefined)[] | undefined;
};

export const ProjectProcessContext = createContext(
  {} as Props & ProjectProcessContextType
);

export default function ProjectProcess({
  project,
  processGroups,
  inModal,
  isEditable,
  reference,
  setIsSaveable,
}: Props & { reference: React.RefObject<HTMLDivElement> }) {
  const skillObj = useRedux((state) => state.skill?.entities.byId);

  const { sparks } = useContext(ProjectDetailsContext);

  const reflections = useRedux((state) => {
    const allReflections = state.reflection?.entities.allIds.map(
      (id) => state.reflection?.entities.byId[id]
    );

    return allReflections?.filter(
      (r) =>
        r?.project_process?.project_id === project?.id! &&
        r?.created_by === state.authentication?.user?.id
    );
  });

  // get other sparks linked to activities
  const reflectionSparks = reflections?.map((r) => r?.sparks).flat();

  // accumulate all sparks
  const allProjectSparks =
    // check if reflectionSparks and projectSparks are not undefined
    reflectionSparks?.length && sparks?.length
      ? // if so, check if there are any duplicates and remove them
        [...reflectionSparks, ...sparks].reduce((acc, current) => {
          const x = acc.find((item: any) => item.id === current?.id!);
          if (!x) {
            return acc.concat([current!]);
          }
          return acc;
        }, [] as SparkEntity[])
      : reflectionSparks?.length
      ? reflectionSparks
      : sparks;

  const filteredProjectSparks = allProjectSparks?.filter(
    (s: any) => !s?.notInGoals
  );

  // pass in all sparks, skillObj and reflections so we can get all spark in end point description
  const groupedSparks = groupSparksBySkill(
    sparks,
    skillObj,
    reflections as ReflectionEntity[]
  );
  return (
    <ProjectProcessContext.Provider
      value={{
        project,
        processGroups,
        inModal,
        isEditable,
        setIsSaveable,
        groupedSparks,
        skillObj,
        filteredProjectSparks,
      }}
    >
      <div
        styleName="outer-wrapper"
        style={{ width: inModal ? 'initial' : '' }}
      >
        <div
          styleName={`wrapper ${isEditable ? 'edit-mode' : ''}`}
          ref={reference}
        >
          <Start />
          {processGroups && processGroups?.length > 2 && <ProcessSteps />}
          <End />
          <GridFiller noWidth />
        </div>
      </div>
    </ProjectProcessContext.Provider>
  );
}
